import React, { useState, useEffect } from 'react';
import {
  Users, Plus, Search, Filter, Phone, Mail, Building2, Tag, Clock,
  ChevronRight, CheckCircle, XCircle, MessageSquare, Calendar, Star,
  ArrowUpRight, MoreVertical, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import leadsApi from '@/api/leads';

const stageConfig = {
  new: { label: 'New', color: 'bg-blue-500', textColor: 'text-blue-600' },
  contacted: { label: 'Contacted', color: 'bg-cyan-500', textColor: 'text-cyan-600' },
  qualified: { label: 'Qualified', color: 'bg-amber-500', textColor: 'text-amber-600' },
  proposal: { label: 'Proposal', color: 'bg-purple-500', textColor: 'text-purple-600' },
  negotiation: { label: 'Negotiation', color: 'bg-orange-500', textColor: 'text-orange-600' },
  won: { label: 'Won', color: 'bg-emerald-500', textColor: 'text-emerald-600' },
  lost: { label: 'Lost', color: 'bg-red-500', textColor: 'text-red-600' },
};

const priorityConfig = {
  low: { label: 'Low', color: 'bg-slate-400' },
  medium: { label: 'Medium', color: 'bg-blue-400' },
  high: { label: 'High', color: 'bg-orange-400' },
  urgent: { label: 'Urgent', color: 'bg-red-500' },
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLeadDetail, setShowLeadDetail] = useState(null);
  const [viewMode, setViewMode] = useState('pipeline'); // pipeline or list

  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    priority: 'medium',
    tags: '',
  });

  useEffect(() => {
    loadLeads();
    loadStats();
  }, [stageFilter]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (stageFilter) params.stage = stageFilter;
      const result = await leadsApi.list(params);
      if (result.success) {
        setLeads(result.data);
      }
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await leadsApi.getStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleCreate = async () => {
    if (!newLead.name.trim()) {
      toast.error('Lead name is required');
      return;
    }

    try {
      const data = {
        ...newLead,
        tags: newLead.tags ? newLead.tags.split(',').map(t => t.trim()) : [],
      };
      const result = await leadsApi.create(data);
      if (result.success) {
        toast.success('Lead created!');
        setShowCreateDialog(false);
        setNewLead({ name: '', email: '', phone: '', company: '', priority: 'medium', tags: '' });
        loadLeads();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      const result = await leadsApi.changeStage(leadId, newStage);
      if (result.success) {
        toast.success(`Lead moved to ${stageConfig[newStage]?.label || newStage}`);
        loadLeads();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleConvert = async (leadId) => {
    try {
      const result = await leadsApi.convert(leadId);
      if (result.success) {
        toast.success('Lead converted!');
        loadLeads();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to convert lead');
    }
  };

  const handleMarkLost = async (leadId, reason) => {
    try {
      const result = await leadsApi.markLost(leadId, reason);
      if (result.success) {
        toast.success('Lead marked as lost');
        loadLeads();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const filteredLeads = leads.filter(l =>
    l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (l.email && l.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (l.company && l.company.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group leads by stage for pipeline view
  const pipelineStages = ['new', 'contacted', 'qualified', 'proposal', 'negotiation'];
  const leadsByStage = pipelineStages.reduce((acc, stage) => {
    acc[stage] = filteredLeads.filter(l => l.stage === stage);
    return acc;
  }, {});

  const LeadCard = ({ lead }) => (
    <Card 
      className="mb-2 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => setShowLeadDetail(lead)}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">{lead.name}</p>
              {lead.company && <p className="text-xs text-muted-foreground">{lead.company}</p>}
            </div>
          </div>
          <Badge className={`${priorityConfig[lead.priority]?.color} text-white text-[10px] px-1.5`}>
            {lead.priority}
          </Badge>
        </div>
        <div className="space-y-1 text-xs text-muted-foreground">
          {lead.email && (
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <span className="truncate">{lead.email}</span>
            </div>
          )}
          {lead.phone && (
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              <span>{lead.phone}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Star className="w-3 h-3" />
            <span>Score: {lead.score}</span>
          </div>
          {lead.source_name && (
            <Badge variant="outline" className="text-[10px]">
              {lead.source_name}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Leads</h1>
          <p className="text-muted-foreground mt-1">Manage your sales pipeline</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-lead-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Leads</p>
              <p className="text-2xl font-bold">{stats.total_leads}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Won</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.by_stage?.won?.count || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Lost</p>
              <p className="text-2xl font-bold text-red-600">{stats.by_stage?.lost?.count || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Conversion Rate</p>
              <p className="text-2xl font-bold">{stats.conversion_rate}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Value</p>
              <p className="text-2xl font-bold">${stats.total_value?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters & View Toggle */}
      <div className="flex gap-4 items-center justify-between">
        <div className="flex gap-4 items-center flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-leads"
            />
          </div>
        </div>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="list">List</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Pipeline View */}
      {viewMode === 'pipeline' && (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-view">
          {pipelineStages.map(stage => (
            <div key={stage} className="flex-shrink-0 w-72">
              <div className={`p-3 rounded-t-lg ${stageConfig[stage].color}`}>
                <div className="flex items-center justify-between text-white">
                  <span className="font-medium">{stageConfig[stage].label}</span>
                  <Badge variant="secondary" className="bg-white/20 text-white">
                    {leadsByStage[stage]?.length || 0}
                  </Badge>
                </div>
              </div>
              <div className="bg-muted/30 rounded-b-lg p-2 min-h-[400px]">
                {leadsByStage[stage]?.map(lead => (
                  <LeadCard key={lead.id} lead={lead} />
                ))}
                {leadsByStage[stage]?.length === 0 && (
                  <p className="text-center text-muted-foreground text-sm py-8">
                    No leads in this stage
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full" data-testid="leads-table">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Name</th>
                  <th className="text-left p-4 font-medium">Contact</th>
                  <th className="text-left p-4 font-medium">Stage</th>
                  <th className="text-left p-4 font-medium">Source</th>
                  <th className="text-left p-4 font-medium">Score</th>
                  <th className="text-left p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map(lead => (
                  <tr key={lead.id} className="border-b hover:bg-muted/30">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{lead.name}</p>
                          {lead.company && <p className="text-xs text-muted-foreground">{lead.company}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm">
                        {lead.email && <p>{lead.email}</p>}
                        {lead.phone && <p className="text-muted-foreground">{lead.phone}</p>}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={`${stageConfig[lead.stage]?.color} text-white`}>
                        {stageConfig[lead.stage]?.label}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">{lead.source}</Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-500" />
                        <span>{lead.score}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setShowLeadDetail(lead)}>
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {lead.stage !== 'won' && lead.stage !== 'lost' && (
                            <>
                              <DropdownMenuItem onClick={() => handleConvert(lead.id)}>
                                <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                                Mark as Won
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleMarkLost(lead.id)}>
                                <XCircle className="w-4 h-4 mr-2 text-red-500" />
                                Mark as Lost
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredLeads.length === 0 && (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No leads found</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription>Enter lead contact information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="John Smith"
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                  data-testid="lead-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  placeholder="Acme Corp"
                  value={newLead.company}
                  onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="+1 234 567 8900"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={newLead.priority}
                onValueChange={(value) => setNewLead({ ...newLead, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                placeholder="hot-lead, referral"
                value={newLead.tags}
                onChange={(e) => setNewLead({ ...newLead, tags: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-lead-submit">
              Add Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      {showLeadDetail && (
        <Dialog open={!!showLeadDetail} onOpenChange={() => setShowLeadDetail(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <DialogTitle>{showLeadDetail.name}</DialogTitle>
                  <DialogDescription>{showLeadDetail.company || 'No company'}</DialogDescription>
                </div>
                <Badge className={`ml-auto ${stageConfig[showLeadDetail.stage]?.color} text-white`}>
                  {stageConfig[showLeadDetail.stage]?.label}
                </Badge>
              </div>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-6 py-4">
              <div className="space-y-4">
                <h4 className="font-medium">Contact Info</h4>
                {showLeadDetail.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${showLeadDetail.email}`} className="text-primary hover:underline">
                      {showLeadDetail.email}
                    </a>
                  </div>
                )}
                {showLeadDetail.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${showLeadDetail.phone}`} className="text-primary hover:underline">
                      {showLeadDetail.phone}
                    </a>
                  </div>
                )}
                {showLeadDetail.company && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span>{showLeadDetail.company}</span>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <h4 className="font-medium">Lead Info</h4>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span>Score: {showLeadDetail.score}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${priorityConfig[showLeadDetail.priority]?.color} text-white`}>
                    {showLeadDetail.priority} priority
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Created {new Date(showLeadDetail.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            {showLeadDetail.tags && showLeadDetail.tags.length > 0 && (
              <div className="flex gap-2 flex-wrap py-2">
                {showLeadDetail.tags.map(tag => (
                  <Badge key={tag} variant="secondary">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {showLeadDetail.stage !== 'won' && showLeadDetail.stage !== 'lost' && (
              <DialogFooter>
                <Button variant="outline" onClick={() => handleMarkLost(showLeadDetail.id)}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Mark Lost
                </Button>
                <Button onClick={() => handleConvert(showLeadDetail.id)}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Convert to Won
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
